import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Common/Header';
import ExchangeRateSection from '../components/Accounts/ExchangeRateSection';
import LabelingFeeSection from '../components/Accounts/LabelingFeeSection';
import ExchangeRateHistorySection from '../components/Accounts/ExchangeRateHistorySection';
import SearchFilter from '../components/Accounts/SearchFilter';
import UserTable from '../components/Accounts/UserTable';
import UserFormModal from '../components/Accounts/UserFormModal';
import PasswordModal from '../components/Accounts/PasswordModal';
import DeleteConfirmModal from '../components/Accounts/DeleteConfirmModal';
import PermissionGuard from '../components/Common/PermissionGuard';
import { PERMISSIONS } from '../utils/permissions';
import './AccountsPage.css';

const AccountsPage = () => {
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState(null);
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showUserModal, setShowUserModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isEdit, setIsEdit] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    
    if (!token || !user._id) {
      navigate('/login');
      return;
    }
    
    setCurrentUser(user);
    fetchUsers(token);
  }, [navigate]);

  useEffect(() => {
    filterUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [users, searchTerm, roleFilter, statusFilter]);

  const fetchUsers = async (token) => {
    try {
      const response = await fetch('/api/users', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setUsers(data);
      }
    } catch (error) {
      console.error('獲取用戶列表失敗:', error);
    }
  };

  const filterUsers = () => {
    let filtered = [...users];
    
    // 需求 R42：與後端關鍵字搜尋一致，改為不分大小寫的子字串比對（trim 去前後空白）。
    const term = searchTerm.trim().toLowerCase();
    if (term) {
      filtered = filtered.filter(u =>
        (u.name || '').toLowerCase().includes(term) ||
        (u.username || '').toLowerCase().includes(term)
      );
    }
    
    if (roleFilter) {
      filtered = filtered.filter(u => u.role === roleFilter);
    }
    
    if (statusFilter !== '') {
      filtered = filtered.filter(u => u.isActive === (statusFilter === 'true'));
    }
    
    setFilteredUsers(filtered);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
  };

  const handleCreateUser = () => {
    setSelectedUser(null);
    setIsEdit(false);
    setShowUserModal(true);
  };

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setIsEdit(true);
    setShowUserModal(true);
  };

  const handleDeleteUser = (user) => {
    setSelectedUser(user);
    setShowDeleteModal(true);
  };

  const handleResetPassword = (user) => {
    setSelectedUser(user);
    setShowPasswordModal(true);
  };

  const handleUserSubmit = async (formData) => {
    const token = localStorage.getItem('token');
    try {
      const url = isEdit ? `/api/users/${selectedUser._id}` : '/api/users';
      const method = isEdit ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        fetchUsers(token);
        setShowUserModal(false);
        alert(isEdit ? '用戶更新成功' : '用戶建立成功');
      } else {
        const data = await response.json();
        alert(data.message || '操作失敗');
      }
    } catch (error) {
      alert('操作失敗');
    }
  };

  const handlePasswordSubmit = async (password) => {
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`/api/users/${selectedUser._id}/password`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ password })
      });

      if (response.ok) {
        setShowPasswordModal(false);
        alert('密碼修改成功');
      } else {
        alert('密碼修改失敗');
      }
    } catch (error) {
      alert('密碼修改失敗');
    }
  };

  const handleDeleteConfirm = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`/api/users/${selectedUser._id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        fetchUsers(token);
        setShowDeleteModal(false);
        alert('用戶刪除成功');
      } else {
        alert('刪除失敗');
      }
    } catch (error) {
      alert('刪除失敗');
    }
  };

  if (!currentUser) return null;

  return (
    <div className="accounts-page">
      <Header title="帳號管理" user={currentUser} onLogout={handleLogout} onUserUpdate={setCurrentUser} />
      
      <div className="main-container">
        <ExchangeRateSection user={currentUser} />
        <LabelingFeeSection user={currentUser} />

        <section className="section">
          <div className="section-header">
            <h2>
              <span className="section-number">3</span>
              用戶列表
            </h2>
            <PermissionGuard user={currentUser} permission={PERMISSIONS.USER_MANAGE}>
              <button className="btn btn-primary btn-sm" onClick={handleCreateUser}>
                + 新增用戶
              </button>
            </PermissionGuard>
          </div>

          <SearchFilter
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            roleFilter={roleFilter}
            onRoleChange={setRoleFilter}
            statusFilter={statusFilter}
            onStatusChange={setStatusFilter}
          />

          <UserTable
            users={filteredUsers}
            currentUser={currentUser}
            onEdit={handleEditUser}
            onDelete={handleDeleteUser}
            onResetPassword={handleResetPassword}
          />
        </section>

        <ExchangeRateHistorySection />
      </div>

      <UserFormModal
        show={showUserModal}
        onClose={() => setShowUserModal(false)}
        onSubmit={handleUserSubmit}
        user={selectedUser}
        isEdit={isEdit}
      />

      <PasswordModal
        show={showPasswordModal}
        onClose={() => setShowPasswordModal(false)}
        onSubmit={handlePasswordSubmit}
        user={selectedUser}
      />

      <DeleteConfirmModal
        show={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDeleteConfirm}
        user={selectedUser}
      />
    </div>
  );
};

export default AccountsPage;
